import type { IncomingMessage, ServerResponse } from 'node:http'
import { CLOUD_RUN_URLS, GAME_MANAGER_URLS } from '../firebase/firebaseConfigs'
import type { PlaygroundTarget } from '../config/playground'
import { shouldExposeHeadlessAuth } from './devServerSecurity'

/**
 * Middleware path for the Node-side headless key exchange. The browser POSTs
 * here (gated on `config.hasHeadlessAuth`) and receives only a short-lived
 * custom token — the raw key never leaves the dev server.
 */
export const RUNDOT_HEADLESS_AUTH_PATH = '/__rundot-headless-auth'

interface CachedAuthResponse {
  status: number
  body: string
  expiresAt: number
}

const headlessAuthFailureCache = new Map<string, CachedAuthResponse>()
const headlessAuthInflightMap = new Map<string, Promise<{ status: number; body: string }>>()
const NEGATIVE_AUTH_CACHE_TTL_MS = 30_000

export function clearHeadlessAuthFailureCache(): void {
  headlessAuthFailureCache.clear()
  headlessAuthInflightMap.clear()
}

export function parseHeadlessAuthErrorDetails(responseText: string): {
  code?: string
  retryAfter?: number
  message?: string
} {
  try {
    const body = JSON.parse(responseText) as {
      code?: unknown
      retryAfter?: unknown
      message?: unknown
      error?: unknown
    }
    const code = typeof body.code === 'string' ? body.code : undefined
    const retryAfter = typeof body.retryAfter === 'number' ? body.retryAfter : undefined
    const message =
      typeof body.message === 'string'
        ? body.message
        : typeof body.error === 'string'
          ? body.error
          : undefined
    return { code, retryAfter, message }
  } catch {
    return {}
  }
}

async function executeHeadlessAuthExchange(
  headlessKey: string,
  target: PlaygroundTarget,
): Promise<{ status: number; body: string }> {
  const isPlayground = headlessKey.startsWith('pk_')
  const baseUrl = isPlayground ? CLOUD_RUN_URLS[target] : GAME_MANAGER_URLS[target]
  const endpoint = isPlayground ? '/v1/auth/exchange-playground-key' : '/v1/auth/exchange-api-key'
  if (!baseUrl) {
    return {
      status: 500,
      body: JSON.stringify({ error: `No exchange endpoint configured for target "${target}"` }),
    }
  }

  const startTime = Date.now()
  try {
    const response = await fetch(`${baseUrl}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${headlessKey}` },
    })
    const durationMs = Date.now() - startTime
    if (durationMs > 2500) {
      console.warn(
        `\x1b[33m[RUN:playground]\x1b[0m Key exchange took ${durationMs}ms (Cloud Run instance may be cold-starting). Subsequent requests will be fast.`,
      )
    }

    if (!response.ok) {
      const text = await response.text()
      const { code, retryAfter, message } = parseHeadlessAuthErrorDetails(text)

      // Dev-server terminal logging
      if (response.status === 429) {
        console.warn(
          `\x1b[33m[RUN:playground]\x1b[0m Headless auth rate limit exceeded: ${
            message || 'Too many failed attempts'
          }. If errors persist, run 'rundot playground grant-access' to verify your key and wait ${
            retryAfter ?? 30
          }s before retrying.`,
        )
      } else if (code === 'playground-key-expired') {
        console.warn(
          "\x1b[33m[RUN:playground]\x1b[0m Your playground key has expired. Run 'rundot playground grant-access' to renew, then restart the dev server.",
        )
      } else if (code === 'playground-key-revoked') {
        console.warn(
          "\x1b[33m[RUN:playground]\x1b[0m Your playground key was revoked. Run 'rundot playground grant-access' to mint a new key.",
        )
      } else if (code === 'playground-key-invalid') {
        console.warn(
          "\x1b[33m[RUN:playground]\x1b[0m Your playground key is invalid. Run 'rundot playground grant-access' to mint a valid key.",
        )
      } else {
        console.warn(
          `\x1b[33m[RUN:playground]\x1b[0m Headless auth key exchange failed (${response.status}): ${text}`,
        )
      }

      const responsePayload = JSON.stringify({
        error: `Key exchange failed (${response.status}): ${text}`,
        ...(code ? { code } : {}),
        ...(retryAfter !== undefined ? { retryAfter } : {}),
      })

      // Negatively cache terminal auth failures to prevent parallel stampedes
      if (
        response.status === 429 ||
        code === 'playground-key-expired' ||
        code === 'playground-key-revoked' ||
        code === 'playground-key-invalid'
      ) {
        headlessAuthFailureCache.set(headlessKey, {
          status: response.status,
          body: responsePayload,
          expiresAt: Date.now() + NEGATIVE_AUTH_CACHE_TTL_MS,
        })
      }

      return { status: response.status, body: responsePayload }
    }

    // Successful exchange clears any cached failure
    headlessAuthFailureCache.delete(headlessKey)

    const data = (await response.json()) as { data?: { customToken?: unknown } }
    const customToken = data.data?.customToken
    if (typeof customToken !== 'string' || customToken.length === 0) {
      return {
        status: 502,
        body: JSON.stringify({ error: 'Key exchange response missing customToken' }),
      }
    }
    return { status: 200, body: JSON.stringify({ customToken }) }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err)
    console.error('[RUN:playground] Headless auth exchange failed:', message)
    return { status: 500, body: JSON.stringify({ error: message }) }
  }
}

export async function handleHeadlessAuth(
  req: IncomingMessage,
  res: ServerResponse,
  headlessKey: string | undefined,
  target: PlaygroundTarget,
  allowNonRkKey: boolean,
): Promise<void> {
  // Drain the request body (none expected) so the socket can be reused.
  req.resume()

  if (!shouldExposeHeadlessAuth(headlessKey, target, allowNonRkKey) || !headlessKey) {
    res.writeHead(403, { 'Content-Type': 'application/json' })
    res.end(JSON.stringify({ error: 'Headless auth not available' }))
    return
  }

  // Check negative cache
  const cached = headlessAuthFailureCache.get(headlessKey)
  if (cached && Date.now() < cached.expiresAt) {
    res.writeHead(cached.status, { 'Content-Type': 'application/json' })
    res.end(cached.body)
    return
  }

  // Coalesce inflight requests
  let inflight = headlessAuthInflightMap.get(headlessKey)
  if (!inflight) {
    inflight = executeHeadlessAuthExchange(headlessKey, target).finally(() => {
      headlessAuthInflightMap.delete(headlessKey)
    })
    headlessAuthInflightMap.set(headlessKey, inflight)
  }

  const result = await inflight
  res.writeHead(result.status, { 'Content-Type': 'application/json' })
  res.end(result.body)
}
